import React, { useEffect, useState } from 'react';
import { ArrowRight, ArrowUpRight, Check, CreditCard, Hand, Pause, Play, Sparkles } from 'lucide-react';
import './KioskWelcome.css';
import KioskBranding from './KioskBranding';

// `price` = τιμή πλακιδίου που υπολογίζει ο καλών (Ε1α: ίδια με το ταμείο take away)·
// η οθόνη υποδοχής δεν ξέρει μοντέλα τιμής.
interface WelcomeProduct { id: number; name: string; image_url?: string; price: number; }
interface Props {
  language: 'el' | 'en';
  brand: React.ReactNode;
  controls: React.ReactNode;
  products: WelcomeProduct[];
  onStart: () => void;
}
const copy = {
  el: {
    eyebrow: 'Η επόμενη στάση; Κάτι καλό.', title: 'Η ΟΡΕΞΗ', titleEnd: 'ΞΕΚΙΝΑ ΕΔΩ.',
    words: ['ΔΙΑΛΕΞΕ.', 'ΦΤΙΑΞ’ ΤΟ.', 'ΑΠΟΛΑΥΣΕ.'],
    tagline: 'Όπως το θες. Με ένα άγγιγμα.', start: 'Ξεκινήστε εδώ', touch: 'Αγγίξτε για παραγγελία',
    card: 'Πληρωμή με κάρτα', pause: 'Παύση κίνησης', play: 'Ενεργοποίηση κίνησης',
    badge: 'Στα μέτρα σου', menu: 'Το μενού μας', ready: 'Πάμε για κάτι καλό;',
  },
  en: {
    eyebrow: 'Next stop? Something good.', title: 'GOOD MOOD.', titleEnd: 'GREAT FOOD.',
    words: ['PICK IT.', 'MAKE IT YOURS.', 'ENJOY IT.'],
    tagline: 'Your favourites. One touch away.', start: 'Start your order', touch: 'Touch to order',
    card: 'Pay by card', pause: 'Pause motion', play: 'Enable motion',
    badge: 'Made for you', menu: 'Our menu', ready: 'Ready for something good?',
  },
};

/** Attract-mode motion is local to this screen and stops as soon as ordering starts. */
export default function KioskWelcome({ language, brand, controls, products, onStart }: Props) {
  const t = copy[language];
  const [scene, setScene] = useState(0);
  const [paused, setPaused] = useState(() => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
  const [brokenImages, setBrokenImages] = useState<number[]>([]);
  const photos = products.filter(p => p.image_url && !brokenImages.includes(p.id)).slice(0, 5);
  const featured = photos.length ? photos[scene % photos.length] : undefined;

  useEffect(() => {
    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!media) return;
    const onChange = () => setPaused(media.matches);
    media.addEventListener?.('change', onChange);
    return () => media.removeEventListener?.('change', onChange);
  }, []);

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => {
      if (!document.hidden) setScene(value => value + 1);
    }, 4800);
    return () => window.clearInterval(timer);
  }, [paused]);

  return (
    <div className={`kiosk kw ${paused ? 'kw--paused' : ''}`} lang={language}>
      <KioskBranding />
      <div className="kw-grain" aria-hidden="true" />
      <header className="kw-header">{brand}<div className="k-topbar__side">
        <button className="k-iconbtn kw-pause" onClick={() => setPaused(value => !value)} aria-label={paused ? t.play : t.pause} aria-pressed={paused}>
          {paused ? <Play size={22} /> : <Pause size={22} />}
        </button>{controls}
      </div></header>

      <main className="kw-stage">
        <div className="kw-copy">
          <p className="kw-eyebrow"><span />{t.eyebrow}</p>
          <h1>{t.title}<br /><span>{t.titleEnd}</span></h1>
          <div className="kw-word" aria-hidden="true"><span key={scene}>{t.words[scene % t.words.length]}</span></div>
          <p className="kw-tagline">{t.tagline}</p>
          <div className="kw-scene-dots" aria-hidden="true">{t.words.map((word, i) => <span key={word} className={scene % 3 === i ? 'is-active' : ''} />)}</div>
        </div>

        <div className="kw-art" aria-hidden="true">
          <div className="kw-orbit kw-orbit--one" /><div className="kw-orbit kw-orbit--two" />
          <Sparkles className="kw-spark kw-spark--one" /><Sparkles className="kw-spark kw-spark--two" />
          {featured ? (
            <div className="kw-photo" key={featured.id}>
              <img src={featured.image_url} alt="" onError={() => setBrokenImages(previous => [...previous, featured.id])} />
              <div className="kw-photo__caption"><span>{featured.name}</span><strong>€{Number(featured.price || 0).toFixed(2)}</strong></div>
            </div>
          ) : (
            <div className="kw-takeout">
              <div className="kw-receipt"><Check size={30} /><span /><span /><span /><strong>01</strong></div>
              <div className="kw-bag"><div className="kw-bag__handle" /><div className="kw-bag__face"><ArrowUpRight /><span>{t.menu}</span></div></div>
            </div>
          )}
          <div className="kw-sticker"><ArrowUpRight size={30} /><span>{t.badge}</span></div>
          <span className="kw-caption">{t.ready}</span>
        </div>
      </main>

      <div className="kw-ticker" aria-hidden="true"><div className="kw-ticker__track">{[0,1,2,3].map(repeat => <span className="kw-ticker__group" key={repeat}>{t.words.map(word => <span key={word}>{word}<Sparkles size={26} /></span>)}</span>)}</div></div>
      <footer className="kw-footer">
        <button className="kw-start" onClick={onStart} aria-label={t.start}>
          <span className="kw-touch"><Hand size={32} /></span><span className="kw-start__text">{t.touch}<small>{t.start}</small></span><span className="kw-start__arrow"><ArrowRight size={34} /></span>
        </button>
        <div className="kw-footer__meta"><span><CreditCard size={18} />{t.card}</span></div>
      </footer>
    </div>
  );
}
